import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

import { Input } from "common/ui/Input";
import { Select } from "common/ui/Select";

import styles from "./AddressInputs.module.scss";

export interface AddressInputsValues {
  street: string,
  unitNumber: string,
  city: string,
  province: string,
  postalCode: string,
  country: string,
}

type CountryRestriction = "ca";

interface Props {
  label?: string,
  fieldPrefix: string;
  forResidence?: boolean,
  disabled?: boolean,
  readOnly?: boolean,
  countryRestrictions?: CountryRestriction[],
  required?: boolean,
};

function AddressInputs({
  label,
  fieldPrefix,
  forResidence,
  disabled = false,
  readOnly,
  countryRestrictions,
  required,
}: Props) {
  const { t } = useTranslation("common");
  const {
    register,
    setValue,
    resetField,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext();
  const placesLibrary = useMapsLibrary("places");

  const googleInput = useRef<HTMLInputElement | null>(null);
  const { ref, ...rest } = register(`${fieldPrefix}.street`);

  const [autocomplete, setAutocomplete] = useState(null);
  const country = watch(`${fieldPrefix}.country`);

  useEffect(() => {
    if (!placesLibrary) return;

    setAutocomplete(new placesLibrary.Autocomplete(googleInput.current, {
      fields: ["address_components"],
      types: ["address"]
    }));
  }, [placesLibrary]);

  useEffect(() => {
    if (!autocomplete) return;

    if (countryRestrictions?.length > 0) {
      autocomplete.setComponentRestrictions({
        country: countryRestrictions,
      });
    }

    const fillInAddress = () => {
      if (Object.keys(errors).includes(fieldPrefix)) {
        resetField(`${fieldPrefix}.unitNumber`);
        resetField(`${fieldPrefix}.city`);
        resetField(`${fieldPrefix}.province`);
        resetField(`${fieldPrefix}.postalCode`);
        resetField(`${fieldPrefix}.country`);
      }

      const place = autocomplete.getPlace();
      let streetAddress = "";
      let postalCode = "";

      for (const component of place.address_components) {
        const type = component.types[0];

        switch (type) {
          case "subpremise":
            setValue(`${fieldPrefix}.unitNumber`, component.long_name, { shouldDirty: true });
            break;
          case "street_number":
            streetAddress = `${component.long_name} ${streetAddress}`;
            break;
          case "route":
            streetAddress += component.short_name;
            break;
          case "sublocality_level_1":
          case "locality":
            setValue(`${fieldPrefix}.city`, component.long_name, { shouldDirty: true });
            break;
          case "postal_town":
            if (!getValues(`${fieldPrefix}.city`)) {
              setValue(`${fieldPrefix}.city`, component.long_name, { shouldDirty: true });
            }
            break;
          case "administrative_area_level_1":
            setValue(`${fieldPrefix}.province`, component.short_name, { shouldDirty: true });
            break;
          case "country":
            setValue(`${fieldPrefix}.country`, component.long_name, { shouldDirty: true });
            break;
          case "postal_code":
            postalCode = `${component.long_name}${postalCode}`;
            break;
          case "postal_code_suffix":
            postalCode = `${postalCode}-${component.long_name}`;
            break;
        };
      };

      setValue(`${fieldPrefix}.street`, streetAddress, { shouldDirty: true });
      setValue(`${fieldPrefix}.postalCode`, postalCode, { shouldDirty: true });
    };

    const preventSubmitOnEnterKey = (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
      }
    };

    googleInput?.current?.addEventListener("keydown", preventSubmitOnEnterKey);
    const listener = autocomplete.addListener("place_changed", fillInAddress);

    return () => {
      listener.remove();
    }
  }, [autocomplete, errors]);

  return (
    <div className={styles.AddressInputs}>
      <Input
        ref={(e) => {
          ref(e);
          googleInput.current = e;
        }}
        label={label
          ? label
          : forResidence
            ? t("addressInputs.residenceAddress")
            : t("addressInputs.address")
        }
        placeholder={t("addressInputs.addressLine1")}
        errors={errors}
        disabled={disabled}
        {...rest}
        readOnly={readOnly}
        required={required}
      />
      <Input
        placeholder={t("addressInputs.addressLine2")}
        errors={errors}
        disabled={disabled}
        {...register(`${fieldPrefix}.unitNumber`)}
        readOnly={readOnly}
      />
      <Input
        placeholder={t("addressInputs.city")}
        errors={errors}
        disabled={disabled}
        {...register(`${fieldPrefix}.city`)}
        readOnly={readOnly}
        required={required}
      />
      {country === "Canada" ? (
        <ProvinceSelect
          fieldPrefix={fieldPrefix}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
        />
      ) : (
        <Input
          placeholder={t("addressInputs.prov/state")}
          errors={errors}
          disabled={disabled}
          {...register(`${fieldPrefix}.province`)}
          readOnly={readOnly}
          required={required}
        />
      )}
      <div className={styles.Split}>
        <Input
          placeholder={t("addressInputs.postal/zip")}
          errors={errors}
          disabled={disabled}
          {...register(`${fieldPrefix}.postalCode`)}
          readOnly={readOnly}
          required={required}
        />
        {(forResidence || countryRestrictions?.length > 0) ? (
          <CountrySelect
            fieldPrefix={fieldPrefix}
            disabled={disabled}
            readOnly={readOnly}
            countryRestrictions={countryRestrictions}
            required={required}
          />
        ) : (
          <Input
            placeholder={t("addressInputs.country")}
            errors={errors}
            disabled={disabled}
            {...register(`${fieldPrefix}.country`)}
            readOnly={readOnly}
            required={required}
          />
        )}
      </div>
    </div>
  );
}

const countryOptions = {
  ca: "Canada",
  us: "United States",
  uk: "United Kingdom",
}

const provinceOptions = {
  AB: "alberta",
  BC: "britishColumbia",
  MB: "manitoba",
  NB: "newBrunswick",
  NL: "newfoundlandAndLabrador",
  NS: "novaScotia",
  NT: "northwestTerritories",
  NU: "nunavut",
  ON: "ontario",
  PE: "princeEdwardIsland",
  QC: "quebec",
  SK: "saskatchewan",
  YT: "yukon",
}

interface CountrySelectProps {
  fieldPrefix: string,
  disabled: boolean,
  readOnly: boolean,
  countryRestrictions: CountryRestriction[],
  required?: boolean,
}

const CountrySelect = ({
  disabled,
  fieldPrefix,
  readOnly,
  countryRestrictions,
  required,
}: CountrySelectProps) => {
  const { t } = useTranslation("common");
  const { register, formState: { errors } } = useFormContext();

  return (
    <Select
      placeholder={t("addressInputs.selectCountry")}
      errors={errors}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      {...register(`${fieldPrefix}.country`)}
    >
      {countryRestrictions?.length > 0 ? (
        Object
          .keys(countryOptions)
          .filter(key => countryRestrictions.includes(key as CountryRestriction))
          .map(key => (
            <option
              key={key}
              value={countryOptions[key]}
            >
              {countryOptions[key]}
            </option>
          ))
      ) : (
        Object.keys(countryOptions).map(key => (
          <option
            key={key}
            value={countryOptions[key]}
          >
            {countryOptions[key]}
          </option>
        ))
      )}
    </Select>
  )
}

interface ProvinceSelectProps {
  fieldPrefix: string,
  disabled: boolean,
  readOnly: boolean,
  required?: boolean,
}

const ProvinceSelect = ({
  disabled,
  fieldPrefix,
  readOnly,
  required,
}: ProvinceSelectProps) => {
  const { t } = useTranslation("common");
  const { register, formState: { errors } } = useFormContext();

  return (
    <Select
      placeholder={t("addressInputs.province")}
      errors={errors}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      {...register(`${fieldPrefix}.province`)}
    >
      {Object.keys(provinceOptions).map(key => (
        <option
          key={key}
          value={key}
        >
          {t(`addressInputs.provinces.${provinceOptions[key]}`)}
        </option>
      ))}
    </Select>
  )
}

export { AddressInputs };
